
import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Tables } from "@/integrations/supabase/types";
import { usePagination } from "@/hooks/usePagination";

type Partner = Tables<"partners">;
type PartnerInsert = {
  name: string;
  description?: string;
  logo_url?: string;
  website_url?: string;
  display_order?: number;
  status?: "draft" | "published" | "archived";
};

interface UsePartnersManagerProps {
  searchTerm: string;
  filters: {
    status: string;
  };
}

export const usePartnersManager = ({ searchTerm, filters }: UsePartnersManagerProps) => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const fetchPartners = async () => {
    try {
      const { data, error } = await supabase
        .from("partners")
        .select("*")
        .order("display_order", { ascending: true });

      if (error) throw error;
      setPartners(data || []);
    } catch (error: any) {
      console.error('Error fetching partners:', error);
      toast({
        title: "Error",
        description: "Failed to fetch partners: " + error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  // Filter and search partners
  const filteredPartners = useMemo(() => {
    return partners.filter((partner) => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          partner.name.toLowerCase().includes(searchLower) ||
          partner.description?.toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      // Status filter
      if (filters.status && partner.status !== filters.status) {
        return false;
      }

      return true;
    });
  }, [partners, searchTerm, filters]);

  // Pagination
  const pagination = usePagination({
    items: filteredPartners,
    itemsPerPage: 10,
  });

  const handleSave = async (partnerData: PartnerInsert & { id?: string }) => {
    if (saving) return;
    
    setSaving(true);
    console.log('Attempting to save partner:', partnerData);
    
    try {
      if (partnerData.id) {
        // Update existing partner
        console.log('Updating partner with ID:', partnerData.id);
        const { id, ...updateData } = partnerData;
        const { data, error } = await supabase
          .from("partners")
          .update(updateData)
          .eq("id", id)
          .select();

        if (error) {
          console.error('Update error:', error);
          throw error;
        }
        
        console.log('Partner updated successfully:', data);
        toast({ title: "Success", description: "Partner updated successfully!" });
      } else {
        // Create new partner
        console.log('Creating new partner');
        const { data, error } = await supabase
          .from("partners")
          .insert(partnerData)
          .select();

        if (error) {
          console.error('Insert error:', error);
          throw error;
        }
        
        console.log('Partner created successfully:', data);
        toast({ title: "Success", description: "Partner created successfully!" });
      }

      await fetchPartners();
    } catch (error: any) {
      console.error('Save error:', error);
      toast({
        title: "Error",
        description: "Failed to save partner: " + error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const deletePartner = async (partnerId: string) => {
    try {
      const { error } = await supabase
        .from("partners")
        .delete()
        .eq("id", partnerId);

      if (error) throw error;

      toast({ title: "Success", description: "Partner deleted successfully!" });
      fetchPartners();
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to delete partner: " + error.message,
        variant: "destructive",
      });
    }
  };

  return {
    partners,
    loading,
    saving,
    filteredPartners,
    pagination,
    handleSave,
    deletePartner,
    fetchPartners,
  };
};

export type { Partner, PartnerInsert };
